import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CloudSun, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api';
import { formatDateVi } from '@/lib/format';
import { weatherConditionIcon, type WeatherForecastData } from './WeatherForecast';

interface OfficeForecast {
  locationName: string | null;
  forecasts: WeatherForecastData[];
  message?: string;
}

export function OfficeWeatherPanel() {
  // null = theo trụ sở; có giá trị = theo vị trí hiện tại do trình duyệt định vị
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const forecast = useQuery({
    queryKey: ['office-weather', coords],
    queryFn: () =>
      coords
        ? api.get<OfficeForecast>(
            `/booking/weather/current-location?latitude=${coords.latitude}&longitude=${coords.longitude}`,
          )
        : api.get<OfficeForecast>('/booking/weather/office-forecast'),
    staleTime: 10 * 60_000,
  });

  const locate = () => {
    if (coords) {
      setCoords(null);
      return;
    }
    if (!navigator.geolocation) {
      toast.error('Trình duyệt không hỗ trợ định vị');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      (err) => {
        setLocating(false);
        toast.error(
          err.code === err.PERMISSION_DENIED
            ? 'Bạn chưa cho phép truy cập vị trí. Hãy bật quyền vị trí cho trang này.'
            : 'Không xác định được vị trí hiện tại. Vui lòng thử lại.',
        );
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 5 * 60_000 },
    );
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-xs text-slate-600 sm:flex-row sm:items-center sm:gap-3">
      {forecast.isLoading || locating ? (
        <div className="flex items-center gap-1.5" role="status">
          <Loader2 className="size-4 shrink-0 animate-spin text-sky-700" aria-hidden="true" />
          <span>Đang tải thời tiết</span>
        </div>
      ) : forecast.error ? (
        <span
          className="font-semibold text-amber-700"
          role="status"
          title={`Không tải được dự báo thời tiết: ${errorMessage(forecast.error)}`}
        >
          Không tải được thời tiết
        </span>
      ) : (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {(forecast.data?.forecasts ?? []).slice(0, 3).map((day, index) => {
            const WeatherIcon = weatherConditionIcon(day.weatherCode);
            return (
              <div
                key={day.date}
                className={`${index === 0 ? 'flex' : 'hidden sm:flex'} items-center gap-1 rounded bg-sky-50/80 px-1.5 py-1`}
                title={`${formatDateVi(day.date)} · ${day.available ? day.condition ?? 'Chưa rõ' : day.message ?? 'Chưa có dự báo'} · ${day.temperatureC == null ? '—' : `${day.temperatureC.toFixed(1)} °C`}`}
              >
                <span className="whitespace-nowrap text-[10px] text-slate-500">
                  {formatDateVi(day.date)}
                </span>
                <WeatherIcon
                  className="size-4 shrink-0 text-sky-700"
                  aria-label={day.condition ?? 'Thời tiết'}
                  role="img"
                />
                <span className="whitespace-nowrap font-semibold text-slate-700">
                  {day.available && day.temperatureC != null ? `${Math.round(day.temperatureC)}°` : '—'}
                </span>
              </div>
            );
          })}
          {!forecast.data?.forecasts.length && (
            <CloudSun
              className="size-4 shrink-0 text-sky-700"
              aria-label={forecast.data?.message ?? 'Chưa có dữ liệu thời tiết'}
              role="img"
            />
          )}
        </div>
      )}
      <div className="flex min-w-0 items-start gap-1">
        <MapPin className="mt-0.5 size-3.5 shrink-0 text-sky-700" aria-hidden="true" />
        <span className="break-words text-[11px] leading-snug text-slate-600">
          {forecast.data?.locationName ?? 'Trụ sở'}
        </span>
        <button
          type="button"
          onClick={locate}
          disabled={locating}
          title={coords ? 'Bấm để quay lại thời tiết tại trụ sở' : 'Bấm để xem thời tiết tại vị trí hiện tại'}
          aria-label={coords ? 'Quay lại thời tiết tại trụ sở' : 'Xem thời tiết tại vị trí hiện tại'}
          className="ml-0.5 shrink-0 rounded p-1 text-slate-500 transition hover:bg-slate-100 hover:text-sky-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-600 disabled:opacity-70"
        >
          {locating ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <MapPin className="size-3.5" />
          )}
        </button>
      </div>
    </div>
  );
}
